import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, Icon, Badge, Divider, Flex } from "@chakra-ui/react";
import { FaClipboardCheck, FaRulerCombined, FaTools, FaPlug, FaCheckCircle, FaClock, FaPhone, FaFileAlt } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const GOLD_TINT = "rgba(198, 146, 20, 0.08)";

const SolarProcess: React.FC = () => {
  const steps = [
    {
      number: 1,
      title: "Initial Consultation",
      duration: "1 Day",
      icon: FaClipboardCheck,
      tasks: [
        "Free site assessment and roof inspection",
        "Review your energy bills and usage patterns",
        "Discuss your energy goals and budget",
        "Receive preliminary system recommendations"
      ]
    },
    {
      number: 2,
      title: "Custom Design & Quote",
      duration: "3-5 Days",
      icon: FaRulerCombined,
      tasks: [
        "Detailed CAD system design tailored to your roof",
        "3D modeling and shading analysis",
        "Energy production forecasting",
        "Fixed-price quote with financing options",
        "Government rebate and incentive calculations"
      ]
    },
    {
      number: 3,
      title: "Approvals & Permits",
      duration: "1-2 Weeks",
      icon: FaFileAlt,
      tasks: [
        "Submit network connection application",
        "Obtain council building permits",
        "Secure electrical work approvals",
        "Coordinate with your energy retailer",
        "Schedule installation date"
      ]
    },
    {
      number: 4,
      title: "Professional Installation",
      duration: "1-2 Days",
      icon: FaTools,
      tasks: [
        "Install mounting rails and panel framework",
        "Position and secure solar panels",
        "Install inverter and battery (if applicable)",
        "Connect to switchboard and meter",
        "Complete electrical certification",
        "Clean up site and remove all packaging"
      ]
    },
    {
      number: 5,
      title: "Activation & Connection",
      duration: "5-10 Days",
      icon: FaPlug,
      tasks: [
        "Electrical safety inspection by authorities",
        "Network provider meter installation/upgrade",
        "Final connection approval from distributor",
        "System commissioning and testing",
        "Activate monitoring app and portal"
      ]
    },
    {
      number: 6,
      title: "Handover & Support",
      duration: "Ongoing",
      icon: FaCheckCircle,
      tasks: [
        "Complete system walkthrough and training",
        "Provide all warranty documentation",
        "Register products with manufacturers",
        "Ongoing remote monitoring and support",
        "Annual health checks available",
        "25-year performance warranty"
      ]
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/process"
        fallbackTitle="Solar Installation Process"
        fallbackDescription="Step-by-step guide to your solar installation journey from consultation to activation"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 20 }}
        backgroundImage="url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={4} textAlign="center">
            <Badge bg={SOLAR_GOLD} color="white" fontSize="md" px={4} py={2} borderRadius="full">
              SIMPLE & TRANSPARENT
            </Badge>
            <Heading size="2xl" fontWeight="bold">
              Your Solar Installation Journey
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="3xl">
              From first contact to generating clean energy - here's exactly what happens at every step
            </Text>
            <HStack spacing={4} pt={4}>
              <HStack>
                <Icon as={FaClock} />
                <Text fontSize="lg" fontWeight="semibold">4-6 Weeks Total</Text>
              </HStack>
              <Divider orientation="vertical" h="6" borderColor="whiteAlpha.500" />
              <HStack>
                <Icon as={FaPhone} />
                <Text fontSize="lg" fontWeight="semibold">Support 7 Days/Week</Text>
              </HStack>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Timeline Section */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={12} align="stretch">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <Flex direction={{ base: "column", md: "row" }} gap={8} position="relative">
                {/* Step Number & Duration */}
                <VStack spacing={2} minW="180px" align={{ base: "center", md: "flex-start" }}>
                  <Box
                    w={20}
                    h={20}
                    borderRadius="full"
                    bg={NAVY}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="white"
                    fontSize="3xl"
                    fontWeight="bold"
                    shadow="lg"
                  >
                    {step.number}
                  </Box>
                  <Badge bg={SOLAR_GOLD} color="white" fontSize="sm" px={3} py={1}>
                    {step.duration}
                  </Badge>
                </VStack>

                {/* Content Card */}
                <Box
                  flex={1}
                  p={8}
                  bg={GOLD_TINT}
                  borderRadius="xl"
                  border="3px solid"
                  borderColor={SOLAR_GOLD}
                  shadow="md"
                  _hover={{ shadow: "xl", transform: "translateY(-2px)" }}
                  transition="all 0.3s"
                >
                  <HStack spacing={4} mb={4}>
                    <Icon as={step.icon} boxSize={8} color={NAVY} />
                    <Heading size="lg">{step.title}</Heading>
                  </HStack>

                  <VStack spacing={3} align="stretch">
                    {step.tasks.map((task, taskIndex) => (
                      <HStack key={taskIndex} align="flex-start" spacing={3}>
                        <Icon
                          as={FaCheckCircle}
                          color={SOLAR_GOLD}
                          mt={1}
                          boxSize={5}
                        />
                        <Text fontSize="md" color="gray.700">
                          {task}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </Flex>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <Box
                  w="4px"
                  h="40px"
                  bg={SOLAR_GOLD}
                  ml={{ base: "50%", md: "40px" }}
                  transform={{ base: "translateX(-50%)", md: "none" }}
                />
              )}
            </React.Fragment>
          ))}
        </VStack>
      </Container>

      {/* Summary Section */}
      <Box bg="white" py={16}>
        <Container maxW="container.xl">
          <Box
            p={10}
            bg={GOLD_TINT}
            borderRadius="2xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
          >
            <VStack spacing={6} textAlign="center">
              <Heading size="xl" color={NAVY}>What Happens Next?</Heading>
              <Text fontSize="lg" color="gray.700" maxW="3xl">
                Once you submit your enquiry, we'll call you within 24 hours to schedule your free site assessment.
                Our team will guide you through every step, handle all paperwork, and ensure a smooth installation process.
              </Text>
              <HStack spacing={4} pt={4}>
                <Box textAlign="center">
                  <Text fontSize="3xl" fontWeight="bold" color={NAVY}>
                    4-6 Weeks
                  </Text>
                  <Text color="gray.600">Average Timeline</Text>
                </Box>
                <Divider orientation="vertical" h="16" />
                <Box textAlign="center">
                  <Text fontSize="3xl" fontWeight="bold" color={SOLAR_GOLD}>
                    Zero
                  </Text>
                  <Text color="gray.600">Hidden Costs</Text>
                </Box>
                <Divider orientation="vertical" h="16" />
                <Box textAlign="center">
                  <Text fontSize="3xl" fontWeight="bold" color={NAVY}>
                    25 yr
                  </Text>
                  <Text color="gray.600">Performance Warranty</Text>
                </Box>
              </HStack>
            </VStack>
          </Box>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarProcess;
